import { TDocumentDefinitions } from 'pdfmake/interfaces';
import { InvoiceData } from '../models/invoice-data.interface';
import { BasePdfTemplate } from './base-template';
import { getItemUnitName, getSelectedMappingLines } from './helpers/item-display.helper';
/**
 * PTR Enterprise Template - A4 Size
 * Standard professional invoice template
 */
export class A5Version1Template extends BasePdfTemplate {
  override templateName = 'a5-version1';
  override templateVersion = '1.0.0';
  override description = 'New A5 invoice template - A5 size Version 1';
  override pageSize: 'A5' = 'A5';
  override previewImage = 'assets/sampleoutput/ptr-enterprise-sample.png';

  override generatePdfDefinition(data: InvoiceData): TDocumentDefinitions {
    const storename = JSON.parse(localStorage.getItem('storename') || '{}');
    let storeAddress = '';
    if (storename) {
      const addressParts = [
        storename.address,
        storename.address1,
        storename.address2,
        storename.pincode,
      ].filter((part) => part && part.trim());

      storeAddress = addressParts.join(', ');
    }
    console.log(data,'a5 invoice template')

    const businessName =  data.businessName || '';
    const businessAddress = storeAddress || data.businessAddress || '';
    const businessPhone = storename?.mobile || data.businessPhone || '';
    const businessGSTIN = storename?.gstin || data.businessGSTIN || '';

    const customerName = data.customerName || data.party?.name || '';
    const customerPhone =
      data.customerMobile || data.party?.mobile || data.party?.phone || '';
    const customerGSTIN = data.customerGSTIN || data.party?.gstin || '';

    let customerAddress = '';
    if (data.customerAddress) {
      const addressParts = [
        data.customerAddress.line1,
        data.customerAddress.line2,
        data.customerAddress.line3,
        data.customerAddress.city,
        data.customerAddress.state,
        data.customerAddress.pincode || data.customerAddress.zipCode,
      ].filter((part) => part && part.trim());
      customerAddress = addressParts.join(', ');
    } else if (data.party) {
      const addressParts = [
        data.party.address,
        data.party.city,
        data.party.state,
        data.party.pincode,
      ].filter((part) => part && part.trim());
      customerAddress = addressParts.join(', ');
    }

    const formatDate = this.formatDate.bind(this);

    const items = data.items;
    const itemsPerPage = 20;
    const chunks = [];
    for (let i = 0; i < items.length; i += itemsPerPage) {
      chunks.push(items.slice(i, i + itemsPerPage));
    }
    // Determine if this is inter-state (IGST) or intra-state (CGST+SGST)
    const isInterState = this.isInterStateTax(data);

    // Free items summary (shown below the items table only when free items exist)
    let totalFreeQty = 0;
    let totalFreeAmount = 0;
    (items || []).forEach((item: any) => {
      if (Number(item.is_free) === 1) {
        totalFreeQty += Number(item.quantity || 0);
        totalFreeAmount += Number(item.originalprice || item.mrp || 0) * Number(item.quantity || 0);
      }
    });

    // Taxable Amount excluding free items (backend subtotal includes free item amount)
    const freeItemsTaxable = (items || []).reduce((sum: number, item: any) =>
      Number(item.is_free) === 1
        ? sum + Number(item.amount || (item.price || 0) * (item.quantity || 0))
        : sum, 0);
    const taxableAmountExclFree = Math.max(0, Number(data.subtotal || 0) - freeItemsTaxable);

    const salesTables: any = chunks.map((chunk, index) => ({
      // pageBreak: index > 0 ? 'before' : undefined,
      table: {
        headerRows: 2,
        widths: isInterState
          ? [
            22, // S.No
            '*', // Name of Product/Service (flexible)
            38, // HSN/SAC
            22, // Qty
            42, // Rate
            48, // Taxable Value
            20, // IGST %
            33, // IGST Amt
            48, // Total
          ]
          : [
            22, // S.No
            '*', // Name of Product/Service (flexible)
            38, // HSN/SAC
            22, // Qty
            42, // Rate
            48, // Taxable Value
            20, // CGST %
            33, // CGST Amt
            20, // SGST %
            33, // SGST Amt
            48, // Total
          ],
        heights: 13,
        body: [
          // ──────────────────────────
          // HEADER ROW 1
          // ──────────────────────────
          [
            {
              text: 'S.No',
              style: 'tableHeader',
              alignment: 'center',
              bold: true,
              fontSize: 8,
            },
            {
              text: 'Name of Product/ Service',
              style: 'tableHeader',
              alignment: 'left',
              bold: true,
              fontSize: 8,
            },
            {
              text: 'HSN/SAC',
              style: 'tableHeader',
              alignment: 'center',
              bold: true,
              fontSize: 8,
            },
            {
              text: 'Qty',
              style: 'tableHeader',
              alignment: 'center',
              bold: true,
              fontSize: 8,
            },
            {
              text: 'Rate',
              style: 'tableHeader',
              alignment: 'right',
              bold: true,
              fontSize: 8,
            },
            {
              text: 'Taxable Value',
              style: 'tableHeader',
              alignment: 'right',
              bold: true,
              fontSize: 8,
            },
            // Tax columns (IGST or CGST+SGST)
            ...(isInterState
              ? [
                // Inter-state: Show IGST only
                {
                  text: 'IGST',
                  style: 'tableHeader',
                  alignment: 'center',
                  bold: true,
                  fontSize: 8,
                  colSpan: 2,
                },
                {},
              ]
              : [
                // Intra-state: Show CGST + SGST
                {
                  text: 'CGST',
                  style: 'tableHeader',
                  alignment: 'center',
                  bold: true,
                  fontSize: 8,
                  colSpan: 2,
                },
                {},
                {
                  text: 'SGST',
                  style: 'tableHeader',
                  alignment: 'center',
                  bold: true,
                  fontSize: 8,
                  colSpan: 2,
                },
                {},
              ]),
            {
              text: 'Total',
              style: 'tableHeader',
              alignment: 'right',
              bold: true,
              fontSize: 8,
            },
          ],

          // ──────────────────────────
          // HEADER ROW 2 (sub headers)
          // ──────────────────────────
          [
            {}, // S.No
            {}, // Name
            {}, // HSN
            {}, // Qty
            {}, // Rate
            {}, // Taxable Value
            // Tax sub-headers (% and Amt)
            ...(isInterState
              ? [
                // Inter-state: IGST % and Amt
                { text: '%', alignment: 'center', fontSize: 8, bold: true },
                { text: 'Amt', alignment: 'center', bold: true, fontSize: 8 },
              ]
              : [
                // Intra-state: CGST % and Amt, SGST % and Amt
                { text: '%', alignment: 'center', fontSize: 8, bold: true },
                { text: 'Amt', alignment: 'center', bold: true, fontSize: 8 },
                { text: '%', alignment: 'center', fontSize: 8, bold: true },
                { text: 'Amt', alignment: 'center', bold: true, fontSize: 8 },
              ]),
            {}, // Total
          ],

          // ──────────────────────────
          // DATA ROWS
          // ──────────────────────────
          /*...chunk.map((item: any, i: number) => [
            {
              text: i + 1 + index * itemsPerPage,
              alignment: 'center',
              fontSize: 10,
            },
            { text: item.name, alignment: 'left', fontSize: 10 },
            { text: item.hsncode || '', alignment: 'center', fontSize: 10 },
            { text: item.quantity, alignment: 'center', fontSize: 10 },
            { text: '', alignment: 'center', fontSize: 10 },
            { text: '', alignment: 'center', fontSize: 10 },
            {
              text: parseInt(item.price).toFixed(2),
              alignment: 'right',
              fontSize: 10,
            },
            {
              text: (item.quantity * item.price).toFixed(2),
              alignment: 'right',
              fontSize: 10,
            },

            // CGST %
            { text: item.ctax + ' %', alignment: 'center', fontSize: 10 },
            // CGST Amount
            {
              text: parseInt(item.ctaxval).toFixed(2),
              alignment: 'right',
              fontSize: 10,
            },

            // SGST %
            { text: item.stax + ' %', alignment: 'center', fontSize: 10 },
            // SGST Amount
            {
              text: parseInt(item.staxval).toFixed(2),
              alignment: 'right',
              fontSize: 10,
            },
            {
              text:
                item.itaxval +
                item.ctaxval +
                item.staxval +
                item.quantity * item.price,
              alignment: 'right',
              fontSize: 10,
            },
          ]),*/
          ...chunk.map((item: any, i: number) => {
            const toNum = (v: any) => {
              const n = Number(v);
              return isFinite(n) ? n : 0;
            };

            const qty = toNum(item.quantity);
            const price = toNum(item.price);
            // Use item.total which already has discount applied (taxable amount after discount)
            // const taxable = toNum(item.total) || +(qty * price) || 0;
            const taxable = item.discaftertax
              ? (qty * price)
              : (toNum(item.total) || +(qty * price) || 0);

            const rawCtax = toNum(item.cgst); // could be percent or 0
            const rawCtaxVal = toNum(item.cgstval); // could be absolute amount or 0
            const rawStax = toNum(item.sgst);
            const rawStaxVal = toNum(item.sgstval);
            const rawItaxVal = toNum(item.igstval);

            const looksLikePercent = (v: number) => v > 0 && v <= 100;

            // determine CGST percent and value
            const cgstPercent = looksLikePercent(rawCtax)
              ? rawCtax
              : taxable > 0 && rawCtaxVal > 0
                ? +((rawCtaxVal / taxable) * 100)
                : 0;
            const cgstVal =
              rawCtaxVal > 0
                ? rawCtaxVal
                : +(taxable * (cgstPercent / 100)) || 0;

            // determine SGST percent and value
            const sgstPercent = looksLikePercent(rawStax)
              ? rawStax
              : taxable > 0 && rawStaxVal > 0
                ? +((rawStaxVal / taxable) * 100)
                : 0;
            const sgstVal =
              rawStaxVal > 0
                ? rawStaxVal
                : +(taxable * (sgstPercent / 100)) || 0;

            // ITAX is already absolute value in your template (rawItaxVal)
            const itaxVal = rawItaxVal;

            // Determine IGST percent and value
            const rawItax = toNum(item.igst);
            const igstPercent = looksLikePercent(rawItax)
              ? rawItax
              : taxable > 0 && rawItaxVal > 0
                ? +((rawItaxVal / taxable) * 100)
                : 0;
            const igstVal =
              rawItaxVal > 0
                ? rawItaxVal
                : +(taxable * (igstPercent / 100)) || 0;

            // Row total = taxable (after discount) + all taxes
            const rowTotal = +(taxable + cgstVal + sgstVal + itaxVal) || 0;
            // Free items: charge columns (taxable, tax %, tax amt, total) print zero
            const isFreeItem = Number(item.is_free) === 1;

            // Build product name with brand and barcode
            const productNameParts = [];
            if (item.name) {
              productNameParts.push(`${item.name}${Number(item.is_free) === 1 ? ' [FREE]' : ''}`);
            }
            if (item.brand) {
              productNameParts.push(`Brand: ${item.brand}`);
            }
            const barcode = item.sku || item.skuid || '';
            if (barcode) {
              productNameParts.push(`Barcode: ${barcode}`);
            }
            // Unit: Qty column is too narrow (22pt), so show unit as a detail line under the name
            const unitName = getItemUnitName(item);
            if (unitName) {
              productNameParts.push(`Unit: ${unitName}`);
            }
            const productNameText = productNameParts.join('\n');

            // Selected mapping (variants / parameters) — joined into one line because rows use fixed `heights`
            const mappingLines = getSelectedMappingLines(item);
            const nameLine = item.name ? productNameParts[0] : '';
            const restText = (item.name ? productNameParts.slice(1) : productNameParts).join('\n');
            const productNameContent: any =
              mappingLines.length > 0
                ? [
                  ...(nameLine ? [nameLine + '\n'] : []),
                  { text: mappingLines.join(', '), fontSize: 7 },
                  ...(restText ? ['\n' + restText] : []),
                ]
                : productNameText;

            return [
              {
                text: i + 1 + index * itemsPerPage,
                alignment: 'center',
                fontSize: 8,
              },
              {
                // text: productNameText,
                text: productNameContent,
                alignment: 'left',
                fontSize: 8,
                // Enable text wrapping
                noWrap: false,
              },
              { text: item.hsn || '', alignment: 'center', fontSize: 8 },
              { text: qty.toString(), alignment: 'center', fontSize: 8 },
              { text: price.toFixed(2), alignment: 'right', fontSize: 8 },
              { text: isFreeItem ? '0.00' : taxable.toFixed(2), alignment: 'right', fontSize: 8 },

              // Tax columns (IGST or CGST+SGST)
              ...(isInterState
                ? [
                  // Inter-state: Show IGST % and Amt
                  {
                    text: isFreeItem ? '0.00' : igstPercent.toFixed(2),
                    alignment: 'center',
                    fontSize: 8,
                  },
                  {
                    text: isFreeItem ? '0.00' : igstVal.toFixed(2),
                    alignment: 'right',
                    fontSize: 8,
                  },
                ]
                : [
                  // Intra-state: Show CGST % and Amt, SGST % and Amt
                  {
                    text: isFreeItem ? '0.00' : cgstPercent.toFixed(2),
                    alignment: 'center',
                    fontSize: 8,
                  },
                  {
                    text: isFreeItem ? '0.00' : cgstVal.toFixed(2),
                    alignment: 'right',
                    fontSize: 8,
                  },
                  {
                    text: isFreeItem ? '0.00' : sgstPercent.toFixed(2),
                    alignment: 'center',
                    fontSize: 8,
                  },
                  {
                    text: isFreeItem ? '0.00' : sgstVal.toFixed(2),
                    alignment: 'right',
                    fontSize: 8,
                  },
                ]),

              // Total for row
              { text: isFreeItem ? '0.00' : rowTotal.toFixed(2), alignment: 'right', fontSize: 8 },
            ];
          }),
        ],
      },

      layout: {
        hLineWidth: () => 0.5,
        vLineWidth: () => 0.5,
        hLineColor: () => 'black',
        vLineColor: () => 'black',
        paddingLeft: (i: number) => (i === 0 ? 3 : 2),
        paddingRight: (i: number) => (i === 12 ? 3 : 2),
        paddingTop: () => 2,
        paddingBottom: () => 2,
      },

      margin: index > 0 ? [0, -5, 0, 0] : [0, -4, 0, 0],
    }));
    return {
      pageSize: 'A5',
      pageOrientation: 'landscape',
      pageMargins: [10, 130, 10, 10],
      header: (currentPage: number, pageCount: number) => {
        const logoElement = this.getLogoElement(data);
        const headerColumns: any[] = [];
        if (logoElement) {
          headerColumns.push(logoElement);
        }
        headerColumns.push({
          stack: [
            {
              text: businessName,
              fontSize: 13,
              bold: true,
              alignment: 'left',
              margin: logoElement ? [0, 17, 0, 0] : [0, 0, 0, 0],
            },
            {
              text: businessAddress,
              alignment: 'left',
              fontSize: 9,
              margin: [0, 0, 0, 0],
            },
            {
              text: `Mob: ${businessPhone}`,
              alignment: 'left',
              fontSize: 9,
              bold: true,
              margin: [0, 0, 0, 0],
            },
          ],
        });

        return {
          margin: [10, 0, 10, 0],
          stack: [
            {
              columns: headerColumns,
            },
            {
              table: {
                widths: ['*', '*', '*'],
                body: [
                  [
                    {
                      text: `GSTIN : ${businessGSTIN}`,
                      border: [true, true, false, true],
                      fontSize: 9,
                    },
                    {
                      text: 'TAX INVOICE',
                      border: [false, true, false, true],
                      fontSize: 9,
                      alignment: 'center',
                    },
                    {
                      text: 'ORIGINAL FOR RECEIPT',
                      border: [false, true, true, true],
                      fontSize: 9,
                      alignment: 'right',
                    },
                  ],
                ],
              },
              layout: {
                hLineWidth: () => 0.5,
                vLineWidth: () => 0.5,
              },
            },
            {
              table: {
                widths: ['auto', '*', 'auto', '*'],
                body: [
                  [
                    {
                      text: 'M/S',
                      bold: true,
                      fontSize: 9,
                      border: [true, false, false, false],
                    },
                    {
                      text: customerName,
                      fontSize: 9,
                      border: [false, false, false, false],
                    },
                    {
                      text: 'Invoice No.',
                      bold: true,
                      fontSize: 9,
                      border: [true, false, false, false],
                    },
                    {
                      text: data.invoiceNumber,
                      fontSize: 9,
                      border: [false, false, true, false],
                    },
                  ],
                  [
                    {
                      text: 'Address',
                      bold: true,
                      fontSize: 9,
                      border: [true, false, false, false],
                    },
                    {
                      text: customerAddress,
                      fontSize: 9,
                      border: [false, false, false, false],
                    },
                    {
                      text: 'Invoice Date',
                      bold: true,
                      fontSize: 9,
                      border: [true, false, false, false],
                    },
                    {
                      text: formatDate(
                        (data.invoiceDate || data.invoicedate || '') as
                        | string
                        | Date,
                      ),
                      fontSize: 9,
                      border: [false, false, true, false],
                    },
                  ],
                  [
                    {
                      text: 'Phone',
                      bold: true,
                      fontSize: 9,
                      border: [true, false, false, false],
                    },
                    {
                      text: customerPhone,
                      fontSize: 9,
                      border: [false, false, false, false],
                    },
                    {
                      text: '',
                      fontSize: 9,
                      border: [true, false, false, false],
                    },
                    {
                      text: '',
                      fontSize: 9,
                      border: [false, false, true, false],
                    },
                  ],
                  [
                    {
                      text: 'GSTIN',
                      bold: true,
                      fontSize: 9,
                      border: [true, false, false, true],
                    },
                    {
                      text: customerGSTIN,
                      fontSize: 9,
                      border: [false, false, false, true],
                    },
                    {
                      text: '',
                      fontSize: 9,
                      border: [true, false, false, true],
                    },
                    {
                      text: '',
                      fontSize: 9,
                      border: [false, false, true, true],
                    },
                  ],
                ],
              },
              layout: {
                hLineWidth: () => 0.5,
                vLineWidth: () => 0.5,
                hLineColor: () => 'black',
                vLineColor: () => 'black',
              },
              margin: [0, 0, 0, 0],
            },
          ],
        };
      },
      content: [
        ...salesTables,
        // Free Items line (only when free items exist)
        ...(totalFreeQty > 0
          ? [
            {
              text: `Free Items : ${totalFreeQty}   ${totalFreeAmount.toFixed(2)}`,
              bold: true,
              fontSize: 8,
              alignment: 'right',
              margin: [0, 2, 4, 2],
            } as any,
          ]
          : []),
        // After Sales Table Summary and Payment Section
        {
          table: {
            widths: [230, 100, '*'],
            body: [
              [
                {
                  stack: [
                    {
                      text: `Total in words : ${this.numberToWords(
                        this.getGrandTotal(data),
                      ).replace(/\b\w/g, (c) => c.toUpperCase())} ONLY`,
                      bold: true,
                      fontSize: 9,
                    },
                    {
                      text: `Terms and Conditions :${data.terms}`,
                      fontSize: 8,
                      margin: [0, 4, 0, 0],
                    },
                    { text: '\nCustomer Signature', bold: true, fontSize: 9 },
                  ],
                  border: [true, true, true, true],
                },
                {
                  stack: [
                    this.getQrCodeElement(data),
                    {
                      text: 'Pay using UPI',
                      alignment: 'center',
                      fontSize: 8,
                      margin: [0, 4, 0, 0],
                    },
                  ],
                  alignment: 'center',
                  border: [false, true, true, true],
                  margin: [0, 0, 0, 5],
                },
                {
                  table: {
                    widths: ['*', 80],
                    body: [
                      [
                        {
                          text: 'Taxable Amount',
                          fontSize: 8,
                          margin: [4, 2, 0, 6],
                        },
                        {
                          // text: this.formatCurrency(data.subtotal),
                          text: this.formatCurrency(taxableAmountExclFree),
                          alignment: 'right',
                          fontSize: 8,
                          margin: [0, 2, 4, 6],
                        },
                      ],
                      [
                        {
                          text: 'Total Tax',
                          fontSize: 8,
                          margin: [4, 0, 0, 6],
                        },
                        {
                          text: this.formatCurrency(
                            data.taxAmount || data.totalTax || 0,
                          ),
                          alignment: 'right',
                          fontSize: 8,
                          margin: [0, 0, 4, 6],
                        },
                      ],
                      [
                        {
                          text: 'Grand Total',
                          margin: [4, 0, 0, 6],
                          fontSize: 8,
                          bold: true,
                        },
                        {
                          text: this.formatCurrency(
                            data.totalAmount || data.grandTotal || 0,
                          ),
                          alignment: 'right',
                          fontSize: 8,
                          bold: true,
                          margin: [0, 0, 4, 6],
                        },
                      ],
                      // Overall Discount row (only if discount exists)
                      ...(data.overallDiscount && data.overallDiscount > 0
                        ? [
                          [
                            {
                              text: 'Overall Discount',
                              fontSize: 8,
                              margin: [4, 0, 0, 6],
                            },
                            {
                              text: `- ${this.formatCurrency(data.overallDiscount)}`,
                              alignment: 'right',
                              fontSize: 8,
                              margin: [0, 0, 4, 6],
                            },
                          ],
                        ]
                        : []),
                      // Loyalty Discount row (only if loyalty discount exists)
                      ...((Number(data.loyaltyAmount) || 0) > 0
                        ? [
                          [
                            {
                              text: `Loyalty Discount (${Number(data.loyaltyPoints) || 0} pts)`,
                              fontSize: 8,
                              margin: [4, 0, 0, 6],
                            },
                            {
                              text: `- ${this.formatCurrency(Number(data.loyaltyAmount) || 0)}`,
                              alignment: 'right',
                              fontSize: 8,
                              margin: [0, 0, 4, 6],
                            },
                          ],
                        ]
                        : []),
                      // Net Amount row (only if there's overall discount or loyalty discount)
                      ...((data.overallDiscount && data.overallDiscount > 0) ||
                        (Number(data.loyaltyAmount) || 0) > 0
                        ? [
                          [
                            {
                              text: 'Net Amount',
                              margin: [4, 0, 0, 6],
                              fontSize: 8,
                              bold: true,
                            },
                            {
                              text: this.formatCurrency(
                                (data.totalAmount || data.grandTotal || 0) -
                                (data.overallDiscount || 0) -
                                (Number(data.loyaltyAmount) || 0),
                              ),
                              alignment: 'right',
                              fontSize: 8,
                              bold: true,
                              margin: [0, 0, 4, 6],
                            },
                          ],
                        ]
                        : []),
                      [
                        {
                          text: `For ${businessName}`,
                          fontSize: 8,
                          margin: [4, 0, 0, 6],
                        },
                        {
                          text: '(E & O.E)',
                          fontSize: 8,
                          alignment: 'right',
                          margin: [0, 0, 4, 6],
                        },
                      ],
                    ],
                  },
                  layout: {
                    hLineWidth: function (i: any, node: any) {
                      return i === 0 || i === node.table.body.length ? 0 : 0.5;
                    },
                    vLineWidth: function () {
                      return 0; // no vertical borders between columns
                    },
                    hLineColor: function () {
                      return 'black';
                    },
                    paddingLeft: function () {
                      return 0;
                    },
                    paddingRight: function () {
                      return 0;
                    },
                    paddingTop: function () {
                      return 2;
                    },
                    paddingBottom: function () {
                      return 2;
                    },
                  },
                  border: [true, true, true, true],
                  margin: [0, 0, 0, 0],
                },
              ],
            ],
          },
          layout: {
            hLineWidth: () => 0.5,
            vLineWidth: () => 0.5,
            hLineColor: () => 'black',
            vLineColor: () => 'black',
          },
          margin: [0, 0, 0, 0],
        },

        // Bank and footer
        ...(storename?.bankname || storename?.accountnumber || storename?.ifsc
          ? [
            {
              text: `Bank : ${storename?.bankname || 'ICICI'}   A/c No. : ${storename?.accountnumber || '987765678765'}   IFSC : ${storename?.ifsc || 'TH0976'}`,
              fontSize: 8,
              margin: [0, 5, 0, 5],
            },
          ]
          : []),
        {
          text: 'Thank you for shopping with us!',
          alignment: 'left',
          fontSize: 8,
        },
      ],
    };
  }
  // Calculate final amount (grand total - overall discount - loyalty discount)
  private getGrandTotal(invoice: InvoiceData): number {
    // Return the final amount after overall discount and loyalty discount
    return (
      (invoice.totalAmount || invoice.grandTotal || 0) -
      (invoice.overallDiscount || 0) -
      (Number(invoice.loyaltyAmount) || 0)
    );
  }

  private getLogoElement(data: InvoiceData): any | null {
    try {
      const logoData =
        data?.business?.logo ||
        (data as any)?.logo ||
        (data as any)?.companyLogo ||
        (data as any)?.logoBase64;
      if (
        logoData &&
        typeof logoData === 'string' &&
        logoData.trim().length > 0
      ) {
        let logoImg = logoData.trim();
        if (!logoImg.startsWith('data:image/') && !logoImg.startsWith('http')) {
          logoImg = `data:image/png;base64,${logoImg}`;
        }
        return {
          image: logoImg,
          width: 80,
          margin: [0, 0, 0, 0],
        };
      }
    } catch (error) {
      console.error(
        '[A5 Template] Logo image error, continuing without sticking PDF:',
        error,
      );
    }
    return null;
  }

  private getQrCodeElement(data: InvoiceData): any {
    try {
      if (
        data.qrcode &&
        typeof data.qrcode === 'string' &&
        data.qrcode.trim().length > 0
      ) {
        let qrData = data.qrcode.trim();
        if (!qrData.startsWith('data:image/') && !qrData.startsWith('http')) {
          qrData = `data:image/png;base64,${qrData}`;
        }
        return {
          image: qrData,
          fit: [80, 80],
          alignment: 'center',
          border: [false, true, false, true],
          margin: [0, 0, 0, 0],
        };
      }
    } catch (error) {
      console.error('[A5 Template] QR Code image error, continuing without sticking PDF:', error);
    }

    return {
      qr: String(this.getGrandTotal(data).toFixed(2)),
      fit: 80,
      alignment: 'center',
      border: [false, true, false, true],
      margin: [0, 0, 0, 0],
    };
  }

  private numberToWords(num: number): string {
    if (!isFinite(num)) return '';
    const ones = [
      '',
      'One',
      'Two',
      'Three',
      'Four',
      'Five',
      'Six',
      'Seven',
      'Eight',
      'Nine',
    ];
    const tens = [
      '',
      '',
      'Twenty',
      'Thirty',
      'Forty',
      'Fifty',
      'Sixty',
      'Seventy',
      'Eighty',
      'Ninety',
    ];
    const teens = [
      'Ten',
      'Eleven',
      'Twelve',
      'Thirteen',
      'Fourteen',
      'Fifteen',
      'Sixteen',
      'Seventeen',
      'Eighteen',
      'Nineteen',
    ];

    const convertLessThanThousand = (n: number): string => {
      n = Math.floor(n);
      if (n === 0) return '';
      if (n < 10) return ones[n];
      if (n < 20) return teens[n - 10];
      if (n < 100)
        return (
          tens[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + ones[n % 10] : '')
        );
      const h = Math.floor(n / 100);
      const rem = n % 100;
      return (
        ones[h] +
        ' Hundred' +
        (rem !== 0 ? ' ' + convertLessThanThousand(rem) : '')
      );
    };

    // work with positive integer part only
    let n = Math.floor(Math.abs(num));
    if (n === 0) return 'Zero';

    const parts: string[] = [];

    const crore = Math.floor(n / 10000000);
    if (crore) {
      parts.push(convertLessThanThousand(crore) + ' Crore');
      n = n % 10000000;
    }

    const lakh = Math.floor(n / 100000);
    if (lakh) {
      parts.push(convertLessThanThousand(lakh) + ' Lakh');
      n = n % 100000;
    }

    const thousand = Math.floor(n / 1000);
    if (thousand) {
      parts.push(convertLessThanThousand(thousand) + ' Thousand');
      n = n % 1000;
    }

    if (n) {
      parts.push(convertLessThanThousand(n));
    }

    return parts.join(' ').replace(/\s+/g, ' ').trim();
  }

  // Helper methods for tax calculations
  private getTotalCGST(invoice: InvoiceData): number {
    // return (
    //   invoice.cgst ||
    //   invoice.items.reduce(
    //     (sum: any, product: any) =>
    //       sum + (product.cgstval || product.cgstAmount || 0),
    //     0,
    //   )
    // );
    // Fallback item sum excludes free items
    return (
      invoice.cgst ||
      invoice.items.reduce(
        (sum: any, product: any) =>
          Number(product.is_free) === 1
            ? sum
            : sum + (product.cgstval || product.cgstAmount || 0),
        0,
      )
    );
  }

  private getTotalSGST(invoice: InvoiceData): number {
    // return (
    //   invoice.sgst ||
    //   invoice.items.reduce(
    //     (sum: any, product: any) =>
    //       sum + (product.sgstval || product.sgstAmount || 0),
    //     0,
    //   )
    // );
    // Fallback item sum excludes free items
    return (
      invoice.sgst ||
      invoice.items.reduce(
        (sum: any, product: any) =>
          Number(product.is_free) === 1
            ? sum
            : sum + (product.sgstval || product.sgstAmount || 0),
        0,
      )
    );
  }

  private getTotalIGST(invoice: InvoiceData): number {
    // return (
    //   invoice.igst ||
    //   invoice.items.reduce(
    //     (sum: any, product: any) =>
    //       sum + (product.igstval || product.igstAmount || 0),
    //     0,
    //   )
    // );
    // Fallback item sum excludes free items
    return (
      invoice.igst ||
      invoice.items.reduce(
        (sum: any, product: any) =>
          Number(product.is_free) === 1
            ? sum
            : sum + (product.igstval || product.igstAmount || 0),
        0,
      )
    );
  }

  private isInterStateTax(invoice: InvoiceData): boolean {
    const igst = this.getTotalIGST(invoice);
    const cgst = this.getTotalCGST(invoice);
    const sgst = this.getTotalSGST(invoice);

    // If IGST > 0 and CGST/SGST are 0, it's inter-state
    return igst > 0 && cgst === 0 && sgst === 0;
  }
}
